import { Outlet } from 'react-router-dom';
import { assetRequestTiming, isAssetRequestOpen, today } from '@cfl/assets-shared';

import { SidebarInset, SidebarProvider } from '@/components/cargofl-ui';
import { AppSidebar } from '@/components/shell/app-sidebar';
import { PageErrorBoundary } from '@/components/shell/page-error-boundary';
import { TopBar } from '@/components/shell/top-bar';
import { Tour, useTour } from '@/components/tour';
import { useAssetRequestQueue } from '@/lib/asset-requests-api';
import type { NavBadges } from '@/lib/nav';
import { useExitSummary, useMyAssets, useRepairSummary } from '@/lib/operations-api';
import { useRepairRequestQueue } from '@/lib/repair-requests-api';
import { useCan } from '@/lib/session';

/**
 * Authenticated app shell: sidebar + top bar wrapping the routed page.
 *
 * Route protection lives in <RequireAuth> (see App.tsx), not here, so this can assume
 * a signed-in user.
 */
export function AppShell() {
  const canSeeRegister = useCan('viewRegister');
  const tour = useTour();
  const canRunExits = useCan('runExitClearance');

  // Each gated on the permission that reveals the badge it feeds. A plain employee
  // would otherwise fire two requests they are not allowed to make, and get two 403s
  // in the console on every page load.
  const { data: repairs } = useRepairSummary({ enabled: canSeeRegister });
  const { data: exits } = useExitSummary({ enabled: canRunExits });
  const { data: mine } = useMyAssets();
  const { data: reported } = useRepairRequestQueue('', { enabled: canSeeRegister });
  const { data: requested } = useAssetRequestQueue('', { enabled: canSeeRegister });

  const unacknowledged = mine?.filter((a) => a.isOpen && a.acknowledgedAt === null).length ?? 0;

  const badges: NavBadges = {
    // Requests waiting on IT. Red once one has gone past the date it was wanted by:
    // that is somebody who was told they would have a laptop and does not, which is a
    // different thing from a queue with work in it.
    openAssetRequests: (() => {
      const open = requested?.filter((r) => isAssetRequestOpen(r.status)) ?? [];
      if (open.length === 0) return undefined;
      const late = open.some((r) => assetRequestTiming(r.neededBy, today()) === 'LATE');
      return { count: open.length, urgent: late };
    })(),
    // Open repairs AND unreviewed reports: both are work sitting on the Repairs
    // screen, and two separate badges on one nav item would be noise.
    openRepairs: (() => {
      const unreviewed =
        reported?.filter((r) => r.status === 'OPEN' || r.status === 'ACKNOWLEDGED').length ?? 0;
      const total = (repairs?.open ?? 0) + unreviewed;
      return total > 0 ? { count: total } : undefined;
    })(),
    // Red: someone has left the company still holding hardware. That is money walking
    // out of the door, and it is the one count here that is genuinely a problem
    // rather than routine work in progress.
    exitedHoldingAssets: exits?.exitedHoldingAssets
      ? { count: exits.exitedHoldingAssets, urgent: true }
      : undefined,
    unacknowledgedMine: unacknowledged ? { count: unacknowledged } : undefined,
  };

  return (
    <SidebarProvider>
      <AppSidebar badges={badges} />
      <SidebarInset>
        <TopBar onStartTour={tour.start} />
        {/* `min-w-0` for the same reason SidebarInset needs it: without it a wide
            table inflates the flex child and re-breaks horizontal scrolling one level
            down. */}
        <div className="min-w-0 flex-1">
          {/* Around the outlet, not the shell: a page that throws must not take the
              sidebar with it, or there is no way out but a reload. */}
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </div>
      </SidebarInset>

      {/* Inside SidebarProvider so it can measure the sidebar in whichever state it
          is actually in - expanded, rail, or a mobile drawer. */}
      <Tour open={tour.running} onClose={tour.finish} />
    </SidebarProvider>
  );
}
